import {
  formatBreederRating,
  formatLanguages,
  formatLocation,
  useRouter,
} from '@application/utils';
import { useReturnParams } from '@application/utils/hooks/useReturnParams';
import type { BreederListGetBreedersQuery } from '@gql/graphql';
import type { CardItemBreederData } from '@presentation/components';
import { PLACEHOLDER_IMAGE } from '@presentation/constants';
import { useCallback } from 'react';

export const useFormatBreederCard = () => {
  const { language, routeLocationDescriptor, translate } = useRouter();

  const { returnBreed, returnSearch } = useReturnParams();

  const getImage = useCallback(
    (breeder: NonNullable<BreederListGetBreedersQuery['findManyDogBreeder']>[number]) => {
      const foundImgPicture1 = breeder?.imgs?.find((x) => x.type === 'picture_1');
      if (foundImgPicture1?.small) return foundImgPicture1.small;
      const foundImgPicture = breeder?.imgs?.find((x) => ['picture', 'IMG'].includes(x.type));
      if (foundImgPicture?.small) return foundImgPicture.small;
      const foundBreedImg = breeder.breederBreeds
        .flatMap((x) => x.dogBreed?.imgs)
        .find((x) => x?.medium);
      if (foundBreedImg?.medium) return foundBreedImg.medium;
      return PLACEHOLDER_IMAGE;
    },
    [],
  );

  const formatBreeder = useCallback(
    (
      breeder: NonNullable<BreederListGetBreedersQuery['findManyDogBreeder']>[number],
      lazyLoadImage = true,
    ): CardItemBreederData => {
      return {
        image: getImage(breeder) ?? undefined,
        hasLitters: breeder.litters.length > 0,
        dogsPedigree: breeder.dogsPedigree,
        breedClubValidated: breeder.breedClubValidated,
        lastConnectedBadgeType: breeder.lastConnectedType ?? undefined,
        languages: formatLanguages(language, breeder.spokenLanguages),
        location: formatLocation(
          { country: breeder.account?.address?.country, region: breeder.account?.address?.region },
          translate,
        ),
        rating: formatBreederRating(breeder.reviews),
        breeds: breeder.breederBreeds
          .map((breederBreed) => breederBreed?.dogBreed?.translations?.[0].name)
          .join(', '),
        link: routeLocationDescriptor('breeder', {
          params: [breeder.translations?.[0]?.url ?? breeder.id.toString() ?? ''],
          state: {
            returnSearch,
            returnBreed,
          },
        }),
        lazyLoadImage,
      };
    },
    [getImage, language, returnBreed, returnSearch, routeLocationDescriptor, translate],
  );

  return { formatBreeder, getImage };
};
